<template>
  <PageContainer>
    <IntegrationPageHeader
      :tile="definition.tile"
      :name="definition.name"
      :description="definition.description"
      :connected="status.connected"
    >
      <template #actions>
        <Text v-if="!mayChangeConnection" variant="body-small" color="secondary">
          Only an admin can {{ status.connected ? 'disconnect' : 'connect' }} {{ definition.name }}.
        </Text>
        <Button v-else-if="!status.connected" variant="primary" size="medium" @click="openConnect">Connect</Button>
        <Button v-else variant="secondary" appearance="danger" size="medium" @click="openDisconnect">Disconnect</Button>
      </template>
    </IntegrationPageHeader>

    <IntegrationPageLayout>
      <Card size="large">
        <View direction="column" gap="sm">
          <Text variant="h3">About this integration</Text>
          <Text variant="body-medium" color="secondary">{{ aboutCopy }}</Text>
        </View>
      </Card>

      <template #rail>
        <IntegrationDetailsCard :details="details" />
      </template>
    </IntegrationPageLayout>

    <GoogleCalendarConnectModal v-if="isGoogle" ref="googleConnectRef" @connected="refresh" />
    <GoogleCalendarDisconnectModal v-if="isGoogle" ref="googleDisconnectRef" @disconnected="refresh" />
    <AptlyConnectModal v-if="isAptly" ref="aptlyConnectRef" @connected="refresh" />
    <AptlyDisconnectModal v-if="isAptly" ref="aptlyDisconnectRef" @disconnected="refresh" />
  </PageContainer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { Button, Card, PageContainer, Text, View } from '@tallo/design-system';
import { useCompanyStore } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';

import AptlyConnectModal from './aptly/aptly-connect-modal.vue';
import AptlyDisconnectModal from './aptly/aptly-disconnect-modal.vue';
import GoogleCalendarConnectModal from './google/google-calendar-connect-modal.vue';
import GoogleCalendarDisconnectModal from './google/google-calendar-disconnect-modal.vue';
import IntegrationDetailsCard from './integration-details-card.vue';
import IntegrationPageHeader from './integration-page-header.vue';
import IntegrationPageLayout from './integration-page-layout.vue';
import { IntegrationDefinition } from './integrations.config';
import { IntegrationStatus } from './use-integration-status';

const props = defineProps<{ definition: IntegrationDefinition; status: IntegrationStatus }>();

const investorStore = useInvestorStore();
const companyStore = useCompanyStore();

const googleConnectRef = ref<InstanceType<typeof GoogleCalendarConnectModal> | null>(null);
const googleDisconnectRef = ref<InstanceType<typeof GoogleCalendarDisconnectModal> | null>(null);
const aptlyConnectRef = ref<InstanceType<typeof AptlyConnectModal> | null>(null);
const aptlyDisconnectRef = ref<InstanceType<typeof AptlyDisconnectModal> | null>(null);

const isGoogle = computed(() => props.definition.id === 'google-calendar');
const isAptly = computed(() => props.definition.id === 'aptly');
// Aptly feeds the whole company's listings, so connecting it is an admin's call; a calendar is the person's own
const mayChangeConnection = computed(() => !isAptly.value || investorStore.isAdmin);

const aboutCopy = computed(() =>
  isGoogle.value
    ? 'Confirmed showings appear on your Google Calendar as they are booked, and disappear when they are cancelled.'
    : 'Tallo reads your Aptly listing feed and keeps the matching properties in sync, so you only maintain them in one place.',
);

const details = computed(() => [
  { label: 'Status', value: props.status.connected ? 'Connected' : 'Not connected' },
  { label: 'Category', value: props.definition.category },
  ...(props.status.meta ? [{ label: 'Account', value: props.status.meta }] : []),
]);

function openConnect() {
  if (isGoogle.value) googleConnectRef.value?.modalRef?.show();
  if (isAptly.value) aptlyConnectRef.value?.modalRef?.show();
}

function openDisconnect() {
  if (isGoogle.value) googleDisconnectRef.value?.modalRef?.show();
  if (isAptly.value) aptlyDisconnectRef.value?.modalRef?.show();
}

async function refresh() {
  await (isGoogle.value ? investorStore.load() : companyStore.refresh());
}
</script>
